/*
 * Adaptive mega menu custom element.
 *
 * Progressive enhancement: Liquid renders every category as a full column with
 * all links present (the no-JS / SEO state). This element measures the panel
 * and, using MegaMenuLayout.decideLayout (assets/mega-menu-layout.js), toggles:
 *   - is-compact : categories become a clickable left rail + detail panel
 *   - is-narrow  : promos become a vertical list of horizontal mini-cards
 *
 * In compact mode the active category's links are cloned into the detail panel.
 * Reveal is click-only; the first category is shown by default.
 */
if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.cats = Array.from(this.querySelectorAll('.mega-menu__cat'));
        this.detail = this.querySelector('.mega-menu__detail');
        this.promoCount = this.querySelectorAll('.mega-menu__promo').length;
        this.activeIndex = 0;
        this.longestSub = 0;

        // Click-only reveal in compact mode (allow modified clicks to navigate).
        this.cats.forEach((cat, i) => {
          const title = cat.querySelector('.mega-menu__cat-title');
          if (!title) return;
          title.addEventListener('click', (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
            if (this.classList.contains('is-compact') && cat.querySelector('.mega-menu__sublist')) {
              e.preventDefault();
              this.setActive(i);
            }
          });
        });

        this.measure();

        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.layout());
          this.ro.observe(this);
        } else {
          window.addEventListener('resize', () => this.layout());
        }

        // Recompute when the parent <details> opens (size goes 0 -> real).
        const details = this.closest('details');
        if (details) {
          details.addEventListener('toggle', () => {
            if (details.open) this.layout();
          });
        }

        this.layout();
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
      }

      // Measure the widest subitem label on a single line, using the real link font.
      measure() {
        const sample = this.querySelector('.mega-menu__sublist a');
        if (!sample) {
          this.longestSub = 0;
          return;
        }
        const span = document.createElement('span');
        const cs = getComputedStyle(sample);
        span.style.cssText =
          'position:absolute;left:-9999px;top:0;white-space:nowrap;visibility:hidden;pointer-events:none;';
        span.style.fontFamily = cs.fontFamily;
        span.style.fontSize = cs.fontSize;
        span.style.fontWeight = cs.fontWeight;
        span.style.letterSpacing = cs.letterSpacing;
        document.body.appendChild(span);
        let max = 0;
        this.querySelectorAll('.mega-menu__sublist a').forEach((a) => {
          span.textContent = a.textContent.trim();
          if (span.offsetWidth > max) max = span.offsetWidth;
        });
        span.remove();
        this.longestSub = max;
      }

      layout() {
        if (!window.MegaMenuLayout) return;
        if (!this.querySelector('.mega-menu__sublist')) return; // condensed menus stay as-is
        const cs = getComputedStyle(this);
        const avail = this.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
        if (avail <= 0) return; // panel not visible yet
        if (!this.longestSub) this.measure();
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;

        const result = window.MegaMenuLayout.decideLayout({
          mode: 'auto',
          avail: avail,
          longestSub: this.longestSub,
          categoryCount: this.cats.length,
          promoCount: this.promoCount,
          rem: rem,
        });

        this.classList.toggle('is-compact', result.compact);
        this.applyPromoLayout(result.promoCard);

        if (result.compact) this.renderDetail();
        else if (this.detail) this.detail.replaceChildren();
      }

      applyPromoLayout(card) {
        const promos = this.querySelector('.mega-menu__promos');
        if (!promos) return;
        ['block', 'tail', 'compact', 'stack'].forEach((m) => promos.classList.toggle('mm-promos--' + m, card === m));

        // Restructure only when the arrangement actually changes (avoids a
        // ResizeObserver feedback loop). In 'tail', the last 2 promos move into
        // a vertical stack column; otherwise all promos are direct children.
        const hasStack = !!promos.querySelector('.mega-menu__promo-stack');
        const wantStack = card === 'tail';
        if (this._promoCard === card && hasStack === wantStack) return;
        this._promoCard = card;

        const els = Array.from(promos.querySelectorAll('.mega-menu__promo'));
        const frag = document.createDocumentFragment();
        if (wantStack && els.length >= 3) {
          const lead = els.length - 2;
          els.slice(0, lead).forEach((el) => frag.appendChild(el));
          const stack = document.createElement('div');
          stack.className = 'mega-menu__promo-stack';
          els.slice(lead).forEach((el) => stack.appendChild(el));
          frag.appendChild(stack);
        } else {
          els.forEach((el) => frag.appendChild(el));
        }
        promos.replaceChildren(frag);
      }

      setActive(i) {
        this.activeIndex = i;
        this.renderDetail();
      }

      renderDetail() {
        if (!this.detail) return;
        const cat = this.cats[this.activeIndex] || this.cats[0];
        if (!cat) return;
        this.cats.forEach((c, idx) => c.classList.toggle('is-active', idx === this.activeIndex));

        const title = cat.querySelector('.mega-menu__cat-title');
        const sublist = cat.querySelector('.mega-menu__sublist');
        this.detail.replaceChildren();
        if (!sublist) return;

        if (title) {
          const head = document.createElement('a');
          head.className = 'mega-menu__detail-head';
          head.href = title.getAttribute('href') || '#';
          head.textContent = title.textContent.trim();
          this.detail.appendChild(head);
        }
        const list = sublist.cloneNode(true);
        list.classList.add('mega-menu__detail-list');
        this.detail.appendChild(list);
        // Columns are computed by CSS (multi-column), so there is nothing to
        // measure here — the browser fits exactly as many columns as the
        // available width allows and can never overflow into the promos.
      }
    }
  );
}
